import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { DEMO_EMPLOYEE } from "../lib/roles";
import { PageHeader, RequestRoutingPanel, type RoutingStep } from "../components/RequestRouting";
import { StoriesTabStrip } from "../components/StoriesTabStrip";
import { LeaveBalanceChart } from "../components/LeaveBalanceChart";
import { useLeaveData } from "../hooks/useLeaveApi";
import {
  LEAVE_CHARGE_CODES,
  SEED_LEAVE_EMPLOYEE,
  SEED_TEAM_LEAVE,
  buildFyLeaveChart,
  fyWindowContaining,
  isFlexModeAsOf,
  projectionPoints,
  roundHours,
  type LeaveRequest,
} from "../data/leaveSeed";

function routingSteps(r: LeaveRequest): RoutingStep[] {
  const hist = r.history;
  const steps: RoutingStep[] = [
    {
      id: "1",
      title: "Submitted",
      role: "You",
      status: "completed",
      at: hist[0]?.at,
      note: `${r.charge_code} · ${r.hours}h · ${r.start_date}`,
    },
    {
      id: "2",
      title: "Supervisor review",
      role: r.supervisor_name,
      status:
        r.status === "Submitted" || r.status === "With supervisor"
          ? "current"
          : r.status === "Cancelled" && hist.length < 3
            ? "upcoming"
            : "completed",
      at: hist.find((h) => h.status === "With supervisor" || h.status === "Approved")?.at,
    },
    {
      id: "3",
      title:
        r.status === "Cancelled"
          ? "Cancelled"
          : r.status === "Rejected"
            ? "Rejected"
            : "Approved",
      role: r.status === "Cancelled" ? "Requester" : r.supervisor_name,
      status:
        r.status === "Approved" || r.status === "Rejected" || r.status === "Cancelled"
          ? "completed"
          : "upcoming",
      at: hist[hist.length - 1]?.at,
      note: hist[hist.length - 1]?.note,
    },
  ];
  return steps;
}

export function LeavePage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const canApprove = roleIds.includes("ROLE-SUPERVISOR");
  const [tab, setTab] = useState<"request" | "approval">("request");
  const { requests, policy, fy, waking, source, persistRequests, patchRequestStatus } =
    useLeaveData();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState({
    charge_code: "PT-ACCRUED",
    hours: "8",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [chartLeaveType, setChartLeaveType] = useState("PT-ACCRUED");

  if (!moduleIds.includes("leave")) return <Navigate to={homePath} replace />;

  const mine = requests
    .filter((r) => r.employee_id === DEMO_EMPLOYEE.id)
    .sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
  const selected = mine.find((r) => r.id === selectedId) ?? mine[0] ?? null;
  const asOf = "2026-07-27";
  const flexMode = isFlexModeAsOf(policy, asOf);
  const { buckets: fyBuckets, meta: proj } = buildFyLeaveChart(
    SEED_LEAVE_EMPLOYEE,
    policy,
    requests,
    fy,
    asOf,
    chartLeaveType,
  );
  const fyWin = fyWindowContaining(fy, asOf);
  const fyLabel = `${fyWin.start} → ${fyWin.end}`;

  const teamQueue = requests.filter(
    (r) =>
      r.supervisor_id === "emp-ortiz" &&
      (r.status === "Submitted" || r.status === "With supervisor"),
  );

  function submitRequest(e: FormEvent) {
    e.preventDefault();
    if (!form.reason.trim() || !form.start_date) return;
    const id = `lr-${Date.now()}`;
    const now = new Date().toISOString();
    const row: LeaveRequest = {
      id,
      employee_id: DEMO_EMPLOYEE.id,
      employee_name: DEMO_EMPLOYEE.name,
      charge_code: form.charge_code,
      hours: Number(form.hours) || 0,
      start_date: form.start_date,
      end_date: form.end_date || form.start_date,
      reason: form.reason.trim(),
      status: "With supervisor",
      submitted_at: now,
      supervisor_id: SEED_LEAVE_EMPLOYEE.supervisor_id,
      supervisor_name: SEED_LEAVE_EMPLOYEE.supervisor_name,
      current_owner: SEED_LEAVE_EMPLOYEE.supervisor_name,
      history: [
        { at: now, status: "Submitted", by: DEMO_EMPLOYEE.name },
        { at: now, status: "With supervisor", by: "System" },
      ],
    };
    void persistRequests([row, ...requests], row);
    setSelectedId(id);
    setForm((f) => ({ ...f, reason: "", start_date: "", end_date: "" }));
  }

  function cancelRequest(id: string) {
    void patchRequestStatus(id, "Cancelled", {
      by: DEMO_EMPLOYEE.name,
      current_owner: "—",
    });
  }

  function decide(id: string, status: "Approved" | "Rejected") {
    void patchRequestStatus(id, status, {
      by: "Jordan Ortiz",
      note: status,
      current_owner: "—",
    });
  }

  const chartMax = flexMode
    ? Math.max(
        SEED_LEAVE_EMPLOYEE.available_hours_ytd,
        SEED_LEAVE_EMPLOYEE.billable_hours_ytd + SEED_LEAVE_EMPLOYEE.unbillable_hours_ytd,
        1,
      )
    : Math.max(...fyBuckets.map((b) => b.balance), proj.carryMax ?? 0, 1);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leave"
        subtitle="Request leave with timesheet charge codes, track routing, and see FY dual-axis balance (use + ending PTO)."
      />
      {waking && (
        <p className="text-sm text-slate bg-mist border border-black/5 px-3 py-2">
          Demo database is starting — showing local leave data until Aurora is ready.
        </p>
      )}
      {source === "api" && !waking && (
        <p className="text-xs text-slate">Leave state synced to demo Aurora.</p>
      )}

      <StoriesTabStrip
        tabs={[
          { id: "request", label: "Leave Request" },
          ...(canApprove ? [{ id: "approval", label: "Leave Approval" }] : []),
        ]}
        active={tab}
        onChange={(id) => setTab(id as typeof tab)}
      />

      {tab === "request" && (
        <div className="space-y-4">
          <div className="bg-white border border-black/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="font-semibold text-navy">
                {flexMode
                  ? "Utilization (flex leave)"
                  : "Leave balance — company FY"}
              </h2>
              {!flexMode && (
                <label className="text-sm text-navy flex items-center gap-2">
                  Leave type
                  <select
                    className="border border-black/10 px-2 py-1.5 text-sm"
                    value={chartLeaveType}
                    onChange={(e) => setChartLeaveType(e.target.value)}
                  >
                    {LEAVE_CHARGE_CODES.filter((c) => c.labor_type === "PT").map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.title}
                      </option>
                    ))}
                    <option value="ALL">All PT types</option>
                  </select>
                </label>
              )}
            </div>
            {flexMode ? (
              <>
                <p className="text-xs text-slate mb-2">
                  Flex mode effective{" "}
                  {policy.flex_effective_date ?? "immediately"} — accrual/balance hidden;
                  utilization vs company target.
                </p>
                <div className="relative h-36 flex items-end gap-6 px-2">
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-teal/80"
                      style={{
                        height: `${(SEED_LEAVE_EMPLOYEE.billable_hours_ytd / chartMax) * 100}%`,
                      }}
                    />
                    <span className="text-xs text-slate">Billable YTD</span>
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-navy/40"
                      style={{
                        height: `${(SEED_LEAVE_EMPLOYEE.unbillable_hours_ytd / chartMax) * 100}%`,
                      }}
                    />
                    <span className="text-xs text-slate">Unbillable YTD</span>
                  </div>
                  <div
                    className="absolute left-0 right-0 border-t-2 border-dashed border-gold"
                    style={{
                      bottom: `${(policy.utilization_target_pct / 100) * 100}%`,
                    }}
                    title={`Target ${policy.utilization_target_pct}% billable`}
                  />
                </div>
                <p className="text-xs text-slate mt-2">
                  Billable{" "}
                  {Math.round(
                    (SEED_LEAVE_EMPLOYEE.billable_hours_ytd /
                      SEED_LEAVE_EMPLOYEE.available_hours_ytd) *
                      100,
                  )}
                  % · company target {policy.utilization_target_pct}% (gold line)
                </p>
              </>
            ) : (
              <>
                <LeaveBalanceChart buckets={fyBuckets} asOf={asOf} fyLabel={fyLabel} />
                <p className="text-xs text-slate mt-2">
                  FY {fy.fy_start_month}/{fy.fy_start_day} start · tenure {proj.years} yrs ·{" "}
                  {proj.annual} hrs/yr accrual ({roundHours(proj.perPay)}/pay) · projected FY-end
                  balance {proj.yearEnd} hrs · max carryover {proj.carryMax ?? "—"} hrs
                </p>
              </>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <form onSubmit={submitRequest} className="bg-white border border-black/5 p-4 space-y-3">
              <h2 className="font-semibold text-navy">New leave request</h2>
              <label className="block text-sm font-medium text-navy">
                Charge code
                <select
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={form.charge_code}
                  onChange={(e) => setForm((f) => ({ ...f, charge_code: e.target.value }))}
                >
                  {LEAVE_CHARGE_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-navy">
                Hours
                <input
                  type="number"
                  step={0.25}
                  min={0}
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={form.hours}
                  onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-sm font-medium text-navy">
                  Start
                  <input
                    type="date"
                    required
                    className="mt-1 w-full border border-black/10 px-3 py-2"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </label>
                <label className="block text-sm font-medium text-navy">
                  End
                  <input
                    type="date"
                    className="mt-1 w-full border border-black/10 px-3 py-2"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  />
                </label>
              </div>
              <label className="block text-sm font-medium text-navy">
                Reason
                <textarea
                  required
                  className="mt-1 w-full border border-black/10 px-3 py-2 min-h-[80px]"
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                />
              </label>
              <button type="submit" className="px-4 py-2 bg-navy text-white text-sm">
                Submit leave request
              </button>
            </form>

            <div className="space-y-4">
              <div className="bg-white border border-black/5 overflow-hidden">
                <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
                  Leave request history
                </div>
                <ul className="divide-y divide-black/5 max-h-56 overflow-y-auto">
                  {mine.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-teal-soft/40 ${
                          selected?.id === r.id
                            ? "bg-teal-soft border-l-4 border-l-teal"
                            : "border-l-4 border-l-transparent"
                        }`}
                        onClick={() => setSelectedId(r.id)}
                      >
                        <div className="font-medium text-navy">
                          {r.charge_code} · {r.hours}h
                        </div>
                        <div className="text-xs text-slate">
                          {r.start_date} · {r.status}
                          {r.current_owner !== "—" ? ` · with ${r.current_owner}` : ""}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              {selected && (
                <div className="bg-white border border-black/5 p-4 space-y-3">
                  <RequestRoutingPanel title="Request routing" steps={routingSteps(selected)} />
                  {(selected.status === "Submitted" ||
                    selected.status === "With supervisor" ||
                    selected.status === "Approved") && (
                    <button
                      type="button"
                      className="px-3 py-2 text-sm border border-red-500/40 text-red-700"
                      onClick={() => cancelRequest(selected.id)}
                    >
                      Cancel this request
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "approval" && canApprove && (
        <div className="space-y-4">
          {teamQueue.length === 0 ? (
            <p className="text-sm text-slate bg-white border border-black/5 p-4">
              No leave requests awaiting your approval.
            </p>
          ) : (
            teamQueue.map((r) => {
              const emp =
                SEED_TEAM_LEAVE.find((e) => e.employee_id === r.employee_id) ??
                SEED_LEAVE_EMPLOYEE;
              const p = projectionPoints(emp, policy, requests);
              const afterMid = p.mid - r.hours;
              const afterYe = p.yearEnd - r.hours;
              return (
                <div key={r.id} className="bg-white border border-black/5 p-4 space-y-3">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <h2 className="font-semibold text-navy">{r.employee_name}</h2>
                      <p className="text-sm text-slate">
                        {r.charge_code} · {r.hours}h · {r.start_date}
                        {r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}
                      </p>
                      <p className="text-sm text-slate mt-1">{r.reason}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="px-3 py-2 text-sm bg-teal text-white"
                        onClick={() => decide(r.id, "Approved")}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="px-3 py-2 text-sm border border-red-500/40 text-red-700"
                        onClick={() => decide(r.id, "Rejected")}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                  {flexMode ? (
                    <p className="text-sm text-slate">
                      YTD billable {emp.billable_hours_ytd}h / unbillable {emp.unbillable_hours_ytd}h
                      · target {policy.utilization_target_pct}%
                    </p>
                  ) : (
                    <div className="grid sm:grid-cols-4 gap-3 text-sm">
                      <div className="border border-black/5 p-2">
                        <div className="text-xs text-slate">FY taken</div>
                        <div className="font-semibold text-navy">{emp.fy_pt_used}h</div>
                      </div>
                      <div className="border border-black/5 p-2">
                        <div className="text-xs text-slate">Accrued YTD</div>
                        <div className="font-semibold text-navy">{p.accruedYtd}h</div>
                      </div>
                      <div className="border border-black/5 p-2">
                        <div className="text-xs text-slate">Planned (excl. this)</div>
                        <div className="font-semibold text-navy">{Math.max(0, p.future - r.hours)}h</div>
                      </div>
                      <div className="border border-black/5 p-2">
                        <div className="text-xs text-slate">If approved</div>
                        <div className="font-semibold text-navy">
                          mid {afterMid}h · YE {afterYe}h
                        </div>
                      </div>
                    </div>
                  )}
                  <RequestRoutingPanel title="Leave request routing" steps={routingSteps(r)} />
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
